import {useUserStore, useSessionStore, useTransformStore, useRecordingStore} from '@/stores'
import {useBroadcast, useRepository} from '@/composables'
import {recording, transform, toWsMessage} from '@/messages'
import i18next from 'i18next'
import {jsonRpc, mediator} from '@communication'
import {StrategyType} from '@communication/StrategyType'
import {useEventBus} from '@vueuse/core'
import {onMounted, onUnmounted} from 'vue'
import {showChangingStrategy} from './useChangeStrategy'
import {useMessageStore} from '@/stores/messagesStore'

const t = i18next.t.bind(i18next)
let isRecordingAllowed = false

export const useRecording = () => {
  const session = useSessionStore()
  const {addMessage} = useMessageStore()

  const start = async () => {
    const currentStrategy = mediator.strategy
    if (currentStrategy !== StrategyType.SFU) {
      showChangingStrategy()
    }
    mediator.call(StrategyType.SFU)
    addMessage({text: t('meeting.notification.startingRecording')})
    mediator.startRecording(session.conference)
  }

  const stop = async () => {
    addMessage({text: t('meeting.notification.stoppingRecording')})
    mediator.stopRecording()
  }

  return {start, stop}
}

export const useRecordingCallback = () => {
  const user = useUserStore()
  const recordingStore = useRecordingStore()
  const session = useSessionStore()
  const {addMessage} = useMessageStore()
  const transformStore = useTransformStore()
  const {stop} = useRecording()

  const handleRecordingStarted = () => {
    if (user.isAssistant) {
      const {sendWsEvent} = useRepository()
      const {zoom, x, y} = transformStore
      const {width, height} = session.transform.client
      sendWsEvent(toWsMessage(transform.setMessage({zoom, x, y})))
      sendWsEvent(toWsMessage(transform.viewportMessage({width, height})))
    }
    addMessage({text: t('meeting.notification.recordingStarted')})
    recordingStore.start()
  }

  const handleRecordingStopped = () => {
    addMessage({text: t('meeting.notification.recordingStopped')})
    recordingStore.stop()
    if (!isRecordingAllowed) {
      recordingStore.deny()
    }
  }
  const allowRecording = (event: CustomEvent) => {
    if (event.detail.party === 'client' || user.party === 'client') {
      isRecordingAllowed = true
      recordingStore.allow()
    }
  }
  onMounted(() => {
    jsonRpc.on('con:ready', allowRecording)
  })
  onUnmounted(() => {
    jsonRpc.off('con:ready', allowRecording)
  })

  useEventBus('session:participant:disjoined').on((event: any) => {
    if (event.party === 'client' && user.party === 'assistant') {
      isRecordingAllowed = false
      if (recordingStore.isOn) {
        stop()
      } else {
        recordingStore.deny()
      }
    }
  })

  useEventBus('strategy:recording:started').on(handleRecordingStarted)
  useEventBus('strategy:recording:stopped').on(handleRecordingStopped)

  useEventBus('profile:create').on(() => {
    if (user.party === 'assistant' && recordingStore.isOn) {
      const broadcast = useBroadcast()
      broadcast(recording.syncMessage(true))
    }
  })

  useEventBus('recording:sync').on(() => {
    if (!recordingStore.isOn) {
      handleRecordingStarted()
    }
  })
}
